import { useRef } from 'react';
import { useSbCb } from 'components/app/storyboard/_mediator/use-sb-cb';
import { useSbLogger } from 'components/app/storyboard/_mediator/use-sb-logger';
import { useSbPlayerContext, ProgressPayload } from '../_mediator/sb-player.provider';
import { PlayerPayload } from '../sb-player-video';
import { LoaderPayload, useLoader } from './use-loader';

const BUFFER_EXCEPTION: Seconds = 2;
const PARAMS = {
  maxLoaderWait: 90 * 1000,
  maxLoaderTime: 100,
};

export const useLoaderBuffer = (index: Index, playerPayload: PlayerPayload, clipId?: ClipId) => {
  const { sbPause, sbSync } = useSbCb();
  const { sbPlayerGetPayload, sbPlayerReady } = useSbPlayerContext();
  const loaderPayload: LoaderPayload = { src: 'B', index, clipId };
  const logger = useSbLogger()?.loaderBuffer(index, clipId);
  const listen = useRef(false);

  const cb = () => {
    listen.current = false;
    sbPlayerReady(index);
    logger?.onBufferEnd();
  };

  const { onLoaderProgress, runLoader, cancelLoader } = useLoader(cb, PARAMS, loaderPayload, playerPayload);

  const onProgress = (data: ProgressPayload) => {
    if (!playerPayload.isDamping && !playerPayload.isActive) {
      // ws hanging up issue
      logger?.regionOutEx(data.loaded);
      cancel();
      return;
    }
    onLoaderProgress(data);
  };

  const onBuffer = () => {
    const loaded = sbPlayerGetPayload(index)?.loaded;
    if (loaded! > BUFFER_EXCEPTION) {
      logger?.exception(loaded);
      return;
    }
    listen.current = true;
    sbPause();
    sbSync();
  };

  const start = () => {
    logger?.onBuffer();
    runLoader();
  };

  const switchReadyToBuffer = (data: ProgressPayload) => {
    listen.current = true;
    logger?.switch();
    runLoader();
    onProgress(data);
  };

  const cancel = () => {
    listen.current = false;
    cancelLoader();
  };

  return { listen, onProgress, onBuffer, start, cancel, switchReadyToBuffer };
};
